/**
 * Canonical trade-identifier upsert planner (PHASE1-PRE-CATALOGUE-BULK-021C).
 *
 * Uniqueness in the database is (org_id, type, lower(value)).
 * Product-level vs variant-level is variant_id null vs set — never mix scopes.
 * identifiers_not_applicable is not Amazon GTIN exemption.
 */

import { validateIdentifierValue } from "@/lib/products";
import { GOVERNED_MATERIAL_ATTRIBUTE } from "@/lib/catalogue-facts";

export type TradeIdentifierType = "gtin" | "upc" | "ean";

export interface IdentifierRow {
  id: string;
  org_id: string;
  product_id: string | null;
  variant_id: string | null;
  type: string;
  value: string;
}

export type IdentifierWritePlan =
  | { action: "noop"; value: string }
  | { action: "insert"; value: string }
  | { action: "update"; id: string; from: string; value: string }
  | { action: "skip_blank" }
  | { action: "skip_not_applicable" }
  | { action: "reject"; error: string; field: string };

export function sameIdentifierScope(
  row: IdentifierRow,
  productId: string,
  variantId: string | null,
): boolean {
  if ((row.product_id ?? "") !== productId) return false;
  const rowVar = row.variant_id ?? null;
  const wantVar = variantId ?? null;
  return rowVar === wantVar;
}

export function identifierScopeLabel(variantId: string | null): string {
  return variantId ? "variant" : "product";
}

export function planTradeIdentifierWrite(input: {
  type: TradeIdentifierType;
  rawValue: string | null | undefined;
  identifiersNotApplicable: boolean;
  orgId: string;
  productId: string;
  variantId: string | null;
  orgRowsOfType: IdentifierRow[];
}): IdentifierWritePlan {
  const field = input.type;
  const raw = (input.rawValue ?? "").trim();

  if (input.identifiersNotApplicable) {
    if (raw) {
      return {
        action: "reject",
        field,
        error: `${field}: cannot set a barcode when identifiers_not_applicable is true (that flag is not Amazon exemption)`,
      };
    }
    return { action: "skip_not_applicable" };
  }

  if (!raw) return { action: "skip_blank" };

  const invalid = validateIdentifierValue(field, raw);
  if (invalid) return { action: "reject", field, error: `${field}: ${invalid}` };

  const scopeRows = input.orgRowsOfType.filter((row) =>
    row.org_id === input.orgId && sameIdentifierScope(row, input.productId, input.variantId) && row.type.toLowerCase() === field,
  );
  if (scopeRows.length > 1) {
    return {
      action: "reject",
      field,
      error: `${field}: multiple ${identifierScopeLabel(input.variantId)}-level values exist — resolve manually`,
    };
  }

  const want = raw.toLowerCase();
  const valueOwner = input.orgRowsOfType.find(
    (row) => row.org_id === input.orgId && row.type.toLowerCase() === field && row.value.trim().toLowerCase() === want,
  );
  if (valueOwner && !sameIdentifierScope(valueOwner, input.productId, input.variantId)) {
    const other = valueOwner.variant_id
      ? "another variant"
      : valueOwner.product_id === input.productId
        ? "the product-level record"
        : "another product";
    return {
      action: "reject",
      field,
      error: `${field}: already belongs to ${other} — not moved`,
    };
  }

  const existing = scopeRows[0];
  if (existing) {
    if (existing.value.trim().toLowerCase() === want) return { action: "noop", value: existing.value };
    return { action: "update", id: existing.id, from: existing.value, value: raw };
  }
  return { action: "insert", value: raw };
}

export function normalizeImportAttributeName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.toLowerCase() === GOVERNED_MATERIAL_ATTRIBUTE.toLowerCase()) return GOVERNED_MATERIAL_ATTRIBUTE;
  return trimmed;
}

export function formatImportFieldError(sourceLine: number, field: string, reason: string): string {
  const msg = reason.startsWith(`${field}:`) ? reason : `${field}: ${reason}`;
  return `row ${sourceLine} · ${msg}`;
}
